import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { ScrollText, ArrowLeft } from "lucide-react";

const CHAR_LIB_BG_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_adventure-hub-220/artifacts/54ke3cv9_my%20characters.png";

export default function CharacterLibraryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/characters").then((r) => setCharacters(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="relative min-h-screen text-[#f1e0c5] bg-[#151613] bg-cover bg-center bg-fixed" style={{ backgroundImage: `url('${CHAR_LIB_BG_URL}')` }} data-testid="character-library-page">
      <div className="fixed inset-0 bg-black/70 pointer-events-none" data-testid="char-lib-bg-overlay" />
      <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6 border-b border-white/5">
        <Link to="/" className="font-display text-2xl flex items-center gap-2.5">
          <img src="/logo192.png" alt="Call to Arms" className="w-8 h-8 rounded-md" data-testid="brand-logo-icon" />
          Call to Arms<span className="text-[#D90429]">.</span>
        </Link>
        <div className="flex items-center gap-4">
          <span className="text-sm text-[#f1e0c5]/60">{user?.name}</span>
        </div>
      </nav>

      <div className="relative z-10 px-6 md:px-12 py-10 max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <Button data-testid="library-back-button" variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="font-display text-4xl mb-1">My Characters</h1>
            <p className="text-sm text-[#f1e0c5]/50">Every character you've built, across every table.</p>
          </div>
        </div>

        {!loading && characters.length === 0 && (
          <div className="text-center py-20 text-[#f1e0c5]/40" data-testid="library-empty-state">
            <ScrollText className="w-10 h-10 mx-auto mb-4 opacity-40" />
            No characters yet. Join a game and create one from the Sheet tab.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {characters.map((c) => (
            <div key={c.id} data-testid={`character-card-${c.id}`} className="p-6 rounded-2xl bg-black/40 border border-white/10 hover:border-[#D4AF37]/40 transition-colors duration-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#D90429]/20 text-[#D90429]">{c.mode}</span>
                <span className="text-[10px] uppercase tracking-widest text-[#D4AF37]/70">Lvl {c.level || 1}</span>
              </div>
              <h3 className="font-display text-2xl mb-1">{c.name}</h3>
              <p className="text-xs text-[#f1e0c5]/40 mb-4 uppercase tracking-wider">
                In {c.game_name || "Unknown Game"} {c.game_system === "homebrew" && <span className="text-[#D4AF37]">· Homebrew</span>} {c.game_system === "dnd5e" && <span className="text-[#D4AF37]">· D&D 5e</span>}
              </p>
              <Button data-testid={`go-to-game-${c.id}`} onClick={() => navigate(`/game/${c.game_id}`)} variant="secondary" className="w-full rounded-full">
                Open Table
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
